import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, TooltipProps } from 'recharts';
import { AdjustmentBucket } from 'model/salesDashboard';
import styles from './AdjustmentBucketChart.module.scss';

const CHART_COLOR = '#5E8677' as const;
const COMPARE_COLOR = '#AFC3BB' as const;

const MONTH_LABELS: Record<string, string> = {
    '01': 'Jan',
    '02': 'Feb',
    '03': 'Mar',
    '04': 'Apr',
    '05': 'May',
    '06': 'Jun',
    '07': 'Jul',
    '08': 'Aug',
    '09': 'Sep',
    '10': 'Oct',
    '11': 'Nov',
    '12': 'Dec'
};

interface ChartDataPoint {
    month: string;
    monthYear: string;
    compareMonthYear?: string;
    current: number;
    compare?: number;
}

const formatMonthLabel = (rawMonth: string): string => MONTH_LABELS[rawMonth.slice(5, 7)] ?? rawMonth;

const formatMonthYear = (rawMonth: string): string => {
    const monthLabel = MONTH_LABELS[rawMonth.slice(5, 7)];
    const year = rawMonth.slice(0, 4);
    return monthLabel ? `${monthLabel} ${year}` : rawMonth;
};

const formatRangeLabel = (points: { month: string }[]): string | null => {
    if (points.length === 0) return null;
    const first = formatMonthYear(points[0].month);
    const last = formatMonthYear(points[points.length - 1].month);
    return first === last ? first : `${first} – ${last}`;
};

const formatYAxisValue = (value: number): string => {
    if (value >= 1000) return `$${value / 1000}k`;
    return `$${value}`;
};

const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;

    const currentEntry = payload.find(p => p.dataKey === 'current');
    const compareEntry = payload.find(p => p.dataKey === 'compare');
    const point = (currentEntry?.payload ?? compareEntry?.payload) as ChartDataPoint | undefined;

    return (
        <div className={styles.customTooltip}>
            <div className={styles.tooltipContent}>
                {currentEntry?.value != null && point && (
                    <div className={styles.tooltipItem}>
                        <div
                            className={styles.colorDot}
                            style={{ backgroundColor: CHART_COLOR }}
                        />
                        <span className={styles.tooltipLabel}>{point.monthYear}</span>
                        <span className={styles.tooltipValue}>${currentEntry.value.toLocaleString()}</span>
                    </div>
                )}
                {compareEntry?.value != null && point?.compareMonthYear && (
                    <div className={styles.tooltipItem}>
                        <div
                            className={styles.colorDot}
                            style={{ backgroundColor: COMPARE_COLOR }}
                        />
                        <span className={styles.tooltipLabel}>{point.compareMonthYear}</span>
                        <span className={styles.tooltipValue}>${compareEntry.value.toLocaleString()}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export interface AdjustmentBucketChartProps {
    data?: AdjustmentBucket;
    isLoading?: boolean;
    title: string;
}

export const AdjustmentBucketChart: React.FC<AdjustmentBucketChartProps> = ({ data, isLoading, title }) => {
    const chartData: ChartDataPoint[] = data
        ? data.sparkline.map((point, index) => {
              const comparePoint = data.compareSparkline[index];
              return {
                  month: formatMonthLabel(point.month),
                  monthYear: formatMonthYear(point.month),
                  current: point.amount,
                  ...(comparePoint && {
                      compare: comparePoint.amount,
                      compareMonthYear: formatMonthYear(comparePoint.month)
                  })
              };
          })
        : [];

    const hasCompareSeries = (data?.compareSparkline.length ?? 0) > 0;
    const currentRangeLabel = data ? formatRangeLabel(data.sparkline) : null;
    const compareRangeLabel = data ? formatRangeLabel(data.compareSparkline) : null;

    const total = data ? parseFloat(data.total) : 0;
    const changePct = data?.changePct != null ? parseFloat(data.changePct) : null;
    const isPositive = changePct == null || changePct >= 0;
    const changeLabel = changePct == null ? '—' : `${isPositive ? '+' : ''}${Math.round(changePct)}%`;

    return (
        <div className={styles.chartContainer}>
            <div className={styles.chartHeader}>
                <h3 className={styles.chartTitle}>{title}</h3>
                <div className={styles.chartMetrics}>
                    <div className={styles.mainValue}>${total.toLocaleString()}</div>
                    <div className={`${styles.changeValue} ${isPositive ? styles.positive : styles.negative}`}>
                        {changeLabel}
                    </div>
                </div>
            </div>

            <div className={styles.chartWrapper}>
                {isLoading && <div className={styles.chartLoading}>Loading...</div>}
                {!isLoading && chartData.length > 0 && (
                    <ResponsiveContainer
                        width="100%"
                        height={180}
                    >
                        <AreaChart
                            data={chartData}
                            margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
                        >
                            <CartesianGrid
                                strokeDasharray="1 1"
                                horizontal={true}
                                vertical={false}
                                stroke="#f0f0f0"
                            />
                            <XAxis
                                dataKey="month"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11, fill: '#999' }}
                            />
                            <YAxis
                                tickFormatter={formatYAxisValue}
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11, fill: '#999' }}
                                width={35}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            {hasCompareSeries && (
                                <Area
                                    type="monotone"
                                    dataKey="compare"
                                    stroke={COMPARE_COLOR}
                                    fill={COMPARE_COLOR}
                                    fillOpacity={0.08}
                                    strokeWidth={2}
                                    dot={false}
                                    activeDot={{ r: 4, fill: COMPARE_COLOR, strokeWidth: 0 }}
                                />
                            )}
                            <Area
                                type="monotone"
                                dataKey="current"
                                stroke={CHART_COLOR}
                                fill={CHART_COLOR}
                                fillOpacity={0.15}
                                strokeWidth={2}
                                dot={false}
                                activeDot={{ r: 4, fill: CHART_COLOR, strokeWidth: 0 }}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </div>

            <div className={styles.chartLegend}>
                <div className={styles.legendItem}>
                    <div
                        className={styles.legendLine}
                        style={{ backgroundColor: CHART_COLOR }}
                    />
                    <span className={styles.legendLabel}>{currentRangeLabel ?? title}</span>
                </div>
                {hasCompareSeries && compareRangeLabel && (
                    <div className={styles.legendItem}>
                        <div
                            className={styles.legendLine}
                            style={{ backgroundColor: COMPARE_COLOR }}
                        />
                        <span className={styles.legendLabel}>{compareRangeLabel}</span>
                    </div>
                )}
            </div>
        </div>
    );
};
